import { createContext, useCallback, useContext, useEffect, useState, type Context } from 'react';

import { bindAll } from 'bind-event-listener';

export const WindowLocationContext: Context<string | null> = createContext<string | null>(null);

export const useWindowLocation = (): string => {
	const isSsr = typeof window === 'undefined';
	const [location, setLocation] = useState(window.location.href);
	// For easier mocking
	const contextValue = useContext(WindowLocationContext);

	const handleLocationChange = useCallback(() => {
		if (!isSsr && window.location.href !== location) {
			setLocation(window.location.href);
		}
	}, [location, setLocation, isSsr]);

	useEffect(() => {
		if (!isSsr) {
			const originalPushState = window.history.pushState;
			const originalReplaceState = window.history.replaceState;

			window.history.pushState = function (...args) {
				originalPushState.apply(this, args);
				handleLocationChange();
			};

			window.history.replaceState = function (...args) {
				originalReplaceState.apply(this, args);
				handleLocationChange();
			};

			const unbind = bindAll(window, [
				{
					type: 'popstate',
					listener: handleLocationChange,
				},
			]);

			return () => {
				unbind();
				window.history.pushState = originalPushState;
				window.history.replaceState = originalReplaceState;
			};
		}
	}, [handleLocationChange, isSsr]);

	return contextValue ?? location;
};
